import axios from 'axios';

export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8050';
// withCredentials: required so the signed session cookie from Google
// Workspace SSO login is sent on every request (cross-origin: :3000 -> :8050).
const api = axios.create({ baseURL: `${BASE_URL}/api/v1`, withCredentials: true });

// -- Auth (Google Workspace OAuth / SSO) ---------------------------------------
export const AUTH_LOGIN_URL = `${BASE_URL}/api/v1/auth/login`;
export const getCurrentUser = () => api.get('/auth/me');
export const logout    = ()             => api.post('/auth/logout');

// -- Chat ---------------------------------------------------------------------
export const sendMessage = (message, conversationId = null, conversationHistory = [], sources = []) =>
  api.post('/chat', {
    message,
    conversation_id: conversationId,
    conversation_history: conversationHistory,
    sources,
  });

// Streaming variant of sendMessage (Dev, 2026-07-04) — POST /chat now
// returns Server-Sent Events instead of one JSON blob, so the chat window
// can show "Guru is checking Google Search Console..." while the backend's
// tool-calling loop is still running instead of a blank spinner for the
// whole duration. axios doesn't stream response bodies in the browser, so
// this uses fetch() directly; withCredentials -> credentials: 'include' is
// the fetch equivalent, needed for the same signed-cookie SSO session.
//
// callbacks.onStatus(label)         — one call per progress step
// callbacks.onDone({...ChatResponse-shaped data})
// callbacks.onError({kind, status?, type?, message?, traceback?})
//   kind: 'network'  — fetch() itself never got a response (DNS/connection
//                       refused/CORS/offline). No status code exists yet.
//         'http'      — got a non-2xx response before streaming started
//                       (quota 429, bad conversation_id 404, or the FastAPI
//                       global exception handler's 500 — see app/main.py's
//                       debug_exception_handler).
//         'stream'    — the stream connected fine (200 OK) but chat() threw
//                       partway through — see chat.py's on_status/run().
//         'incomplete'— the stream just stopped: no answer, no error event.
//                       Usually a reverse-proxy/load-balancer timing out an
//                       SSE connection that stayed open too long.
export const streamMessage = async (
  message, conversationId = null, conversationHistory = [], sources = [], callbacks = {}, isDocRequest = false
) => {
  const { onStatus = () => {}, onDone = () => {}, onError = () => {} } = callbacks;

  let res;
  try {
    res = await fetch(`${BASE_URL}/api/v1/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        conversation_history: conversationHistory,
        sources,
        is_doc_request: isDocRequest,
      }),
    });
  } catch (networkErr) {
    onError({ kind: 'network', message: networkErr?.message || String(networkErr) });
    return;
  }

  if (!res.ok) {
    // Quota (429) / bad conversation (404) / any other pre-stream failure —
    // these never entered the SSE stream at all (see chat.py: checked
    // before StreamingResponse starts), so they're still a normal JSON
    // error body. app/main.py's global exception handler now puts
    // type/message/traceback on every unhandled 500 too, not just
    // {"detail": "Internal Server Error"}.
    let body = {};
    try { body = await res.json(); } catch { /* no/invalid JSON body */ }
    onError({
      kind:      'http',
      status:    res.status,
      type:      body.type,
      message:   body.message || body.detail,
      traceback: body.traceback,
    });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawTerminalEvent = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const chunks = buffer.split('\n\n');
    buffer = chunks.pop(); // last piece may be incomplete — keep it for next read

    for (const chunk of chunks) {
      const line = chunk.trim();
      if (!line.startsWith('data:')) continue;
      let evt;
      try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }

      if (evt.type === 'status') {
        onStatus(evt.label);
      } else if (evt.type === 'answer') {
        sawTerminalEvent = true;
        onDone(evt.data);
      } else if (evt.type === 'error') {
        sawTerminalEvent = true;
        onError({ kind: 'stream', type: evt.error_type, message: evt.message, traceback: evt.traceback });
      }
    }
  }

  if (!sawTerminalEvent) {
    // Connection closed with neither an answer nor an error ever arriving —
    // the request was accepted (200 OK) but something cut the stream short
    // before chat() finished. Almost always a reverse proxy/load balancer
    // idle- or read-timeout on a slow multi-tool question (check
    // proxy_read_timeout / proxy_buffering if you're behind nginx).
    onError({ kind: 'incomplete' });
  }
};

// -- Chat: daily quota ----------------------------------------------------------
export const getQuota = () => api.get('/chat/quota');

// -- Conversations ------------------------------------------------------------
export const getConversations = () => api.get('/chat/conversations');
export const newConversation = () => api.post('/chat/conversations');
export const getConversationMessages = (id) => api.get(`/chat/conversations/${id}`);
export const deleteConversation = (id) => api.delete(`/chat/conversations/${id}`);
export const renameConversation = (id, title) => api.patch(`/chat/conversations/${id}`, { title });

// -- Knowledge sources --------------------------------------------------------
export const getSources = () => api.get('/knowledge/sources');
export const getKbStats = () => api.get('/knowledge/stats');
export const deleteSource = (sourceId) => api.delete(`/knowledge/sources/${sourceId}`);
export const reingestSource = (sourceId) => api.post(`/knowledge/sources/${sourceId}/reingest`);

export const ingestFile = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/knowledge/ingest/file', form);
};

// Ingest a chat answer as a KB text note
export const addAnswerToKb = (answer, label = 'Chat Answer') =>
  api.post('/knowledge/ingest/text', {
    text: answer,
    label: label.slice(0, 120),
  });

// Single URL
export const ingestLink = (url, label = '') =>
  api.post('/knowledge/ingest/link', { url, label });

// Batch URLs  [ { url, label }, ... ]
export const ingestLinks = (links) =>
  api.post('/knowledge/ingest/links', { links });

// -- Feedback -----------------------------------------------------------------
export const submitFeedback = (conversationId, question, answer, rating, correction = '') =>
  api.post('/chat/feedback', {
    conversation_id: conversationId,
    question,
    answer,
    rating,
    correction,
  });

// -- Admin: Costs -------------------------------------------------------------
export const getCosts = () => api.get('/admin/costs');

// -- Admin: Knowledge Captures ------------------------------------------------
export const getCaptures = (status = 'pending', limit = 50) =>
  api.get(`/admin/captures?status=${status}&limit=${limit}`);

export const promoteCapture = (id, editedResponse = null) =>
  api.post(`/admin/captures/${id}/promote`, { edited_response: editedResponse });

export const quarantineCapture = (id) =>
  api.post(`/admin/captures/${id}/quarantine`);

// -- Admin: Blind Spots -------------------------------------------------------
export const getBlindspots = (days = 7, limit = 20) =>
  api.get(`/admin/blindspots?days=${days}&limit=${limit}`);

export const createDocFromBlindspot = (question, answer) =>
  api.post('/admin/blindspots/create-doc', { question, answer });

// -- Knowledge: free-text note ingest ----------------------------------------
export const ingestText = (text, label) =>
  api.post('/knowledge/ingest/text', { text, label });

// -- Admin: Retag + Sensitive (source-level) ----------------------------------
export const retagSource = (sourceId, label, category) =>
  api.patch(`/admin/sources/${sourceId}/retag`, { label, category });

export const setSourceSensitive = (sourceId, sensitive) =>
  api.post(`/admin/sources/${sourceId}/sensitive`, { sensitive });

// -- Admin: Knowledge Admin management -----------------------------------------
export const getAdmins = () => api.get('/admin/admins');
export const addAdmin = (email) => api.post('/admin/admins', { email });
export const removeAdmin = (email) => api.delete(`/admin/admins/${encodeURIComponent(email)}`);
export const setAdminNotifications = (email, enabled) =>
  api.patch(`/admin/admins/${encodeURIComponent(email)}/notifications`, { enabled });

// -- Admin: Project Manager management (Dev, 2026-07-03) -----------------------
// Admin-only (admin.router) — used by the Admin Panel's Project Managers
// tab, which only a Knowledge Admin can even reach in the first place.
export const getProjectManagers = () => api.get('/admin/project-managers');
export const addProjectManager = (email, name) => api.post('/admin/project-managers', { email, name });
export const removeProjectManager = (email) =>
  api.delete(`/admin/project-managers/${encodeURIComponent(email)}`);

// Read-only list of Project Managers for the Register/Edit forms' manager
// dropdown (Dev, 2026-07-04) — lives on projects.router (require_admin_or_pm)
// instead of admin.router, so a Project Manager viewing the Projects panel
// can actually populate this dropdown too, not just a Knowledge Admin. See
// GET /api/v1/projects/managers in app/api/routes/projects.py.
export const getProjectManagersForDropdown = () => api.get('/projects/managers');

// -- Admin: Projects (Project Registry & Access Checklist) ---------------------
// Dev, 2026-06-23: listKarmaProjects()/syncProjects() (the synced Karma
// 1.0+2.0 bulk list) are gone — Guru is the parent registry now, and every
// status check below already queries Karma 1.0/2.0 live. See
// app/core/db.py's comment in the backend for the removed karma_project_cache.

// Register a project by name (ga4PropertyId/managerName/managerEmail/
// gscSiteUrl/executives optional) — creates one checklist row per connector
// and runs the live check immediately, so the response's `connectors` array
// already reflects real status, not a guess. managerName/managerEmail
// (Dev, 2026-06-22; renamed/repurposed from ownerName/ownerEmail on
// 2026-06-25) name who else (besides Knowledge Admins) gets pending-access
// emails for this project — that's the ONLY email recipient besides admins.
// executives (Dev, 2026-06-25) is an optional array of { name, email }
// objects — additional people who help handle the project; they're tracked
// for visibility only and are never emailed. Pass null/omit to leave
// executives unset (registration), or an array (including []) to fully
// replace the list. gscSiteUrl (Dev, 2026-06-25 — renamed/repurposed from
// the old websiteUrl field) is the admin's own entry of the project's
// actual Search Console property; it's checked live against our service
// account's GSC access immediately, and also still doubles as a fallback
// search term for Karma 1.0 and Google Sheets.
// projectType (Dev, 2026-06-26): 'seo' (default) or 'gmb' — which tab the
// project is being registered from. For 'gmb', the backend doesn't require
// gscSiteUrl and only creates a Karma 1.0 checklist row, so callers
// registering a GMB project should simply omit gscSiteUrl/ga4PropertyId
// (null is fine — they're ignored for gmb either way).
export const registerProject = (projectName, ga4PropertyId, managerName = null, managerEmail = null, gscSiteUrl = null, executives = null, projectType = 'seo', aliases = null) =>
  api.post('/projects/register', {
    project_name: projectName,
    ga4_property_id: ga4PropertyId || null,
    manager_name: managerName || null,
    manager_email: managerEmail || null,
    gsc_site_url: gscSiteUrl || null,
    executives: executives || null,
    project_type: projectType || 'seo',
    aliases: aliases || null,
  });

// All registered projects with their rolled-up checklist — list view.
export const getProjectRegistry = () => api.get('/projects/registry');

// One project's checklist (Sheets rows rolled up) — detail view.
export const getProjectChecklist = (projectKey) => api.get(`/projects/${encodeURIComponent(projectKey)}/checklist`);

// Run all five AccessCheckers now — "Re-check now" button.
export const recheckProject = (projectKey, projectName = null) =>
  api.post(`/projects/${encodeURIComponent(projectKey)}/recheck`, projectName ? { project_name: projectName } : {});

// Edit a registered project — display name / GA4 id / manager name /
// manager email / GSC Site URL / executives (Dev, 2026-06-22; ownerName/
// ownerEmail renamed to managerName/managerEmail and executives added on
// 2026-06-25; gscSiteUrl renamed from websiteUrl on 2026-06-25). Only the
// keys present in `fields` are changed; pass an empty string to clear a
// scalar field. fields can include: displayName, ga4PropertyId,
// managerName, managerEmail, gscSiteUrl, executives, projectType.
// executives, if present, fully replaces the project's executive list —
// pass an array of { name, email } objects, or [] to clear all executives;
// omit/null to leave the existing list untouched. projectType (Dev,
// 2026-06-27) — UNLIKE registerProject() above, this one IS changeable
// post-registration: pass 'seo' | 'gmb' | 'seo_gmb' to move the project
// between types (backend adds any newly-required connector rows and
// re-checks them immediately); omit/null to leave the type untouched.
export const updateProject = (projectKey, fields = {}) =>
  api.patch(`/projects/${encodeURIComponent(projectKey)}`, {
    display_name: fields.displayName ?? null,
    ga4_property_id: fields.ga4PropertyId ?? null,
    manager_name: fields.managerName ?? null,
    manager_email: fields.managerEmail ?? null,
    gsc_site_url: fields.gscSiteUrl ?? null,
    executives: fields.executives ?? null,
    project_type: fields.projectType ?? null,
    // aliases: undefined = leave untouched; null or [] = clear; array = replace
    ...(fields.aliases !== undefined ? { aliases: fields.aliases } : {}),
  });

// Delete a registered project + all of its checklist rows (Dev, 2026-06-22).
export const deleteProject = (projectKey) =>
  api.delete(`/projects/${encodeURIComponent(projectKey)}`);

// Manual override of one checklist row (Phase 7).
export const updateChecklistItem = (itemId, status, resourceLabel = null) =>
  api.patch(`/projects/items/${itemId}`, { status, resource_label: resourceLabel });

// Manually run the nightly drift-check + pending-digest job now (QA).
export const runNightlyJob = () => api.post('/projects/run-nightly-job');

// -- Admin: Project Documents (Dev, 2026-06-25) --------------------------------
// Onboarding Documents, Client Requirements, and Minutes of Meeting, scoped to
// one project (Documents panel on the project detail page). docType: one of
// 'onboarding' | 'requirements' | 'mom' | 'other'. meetingDate (YYYY-MM-DD) is
// required only when docType === 'mom' — lets chat answer "last MoM with
// <project>" by recency instead of similarity ranking alone.
export const uploadProjectDocument = (projectKey, file, docType, meetingDate = '') => {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_type', docType);
  if (meetingDate) form.append('meeting_date', meetingDate);
  return api.post(`/projects/${encodeURIComponent(projectKey)}/documents`, form);
};

export const getProjectDocuments = (projectKey) =>
  api.get(`/projects/${encodeURIComponent(projectKey)}/documents`);

export const deleteProjectDocument = (projectKey, sourceId) =>
  api.delete(`/projects/${encodeURIComponent(projectKey)}/documents/${encodeURIComponent(sourceId)}`);

// -- Broadcast Notifications (Dev, 2026-07-24) ---------------------------------
export const draftBroadcast = (subject, instructions) =>
  api.post('/admin/broadcasts/draft', { subject, instructions });

export const chatIntentBroadcast = (message) =>
  api.post('/admin/broadcasts/chat-intent', { message });

// ── Query History (Admin only) ───────────────────────────────────────────────
export const getQueryHistoryUsers = () =>
  api.get('/admin/query-history/users');

export const getQueryHistoryStats = (email) =>
  api.get(`/admin/query-history/stats/${encodeURIComponent(email)}`);

export const getQueryHistory = (email, page = 1, limit = 50, connector = undefined, dateFrom = undefined, dateTo = undefined) =>
  api.get(`/admin/query-history/${encodeURIComponent(email)}`, {
    params: {
      page,
      limit,
      ...(connector ? { connector } : {}),
      ...(dateFrom  ? { date_from: dateFrom } : {}),
      ...(dateTo    ? { date_to:   dateTo   } : {}),
    },
  });

export const deleteQueryHistoryEntry = (id) =>
  api.delete(`/admin/query-history/entry/${id}`);

// Today's live query feed (10PM–10PM window)
export const getTodayQueries = (page = 1, limit = 50, role = undefined, connector = undefined, search = undefined) =>
  api.get('/admin/query-history/today', {
    params: {
      page,
      limit,
      ...(role      ? { role }      : {}),
      ...(connector ? { connector } : {}),
      ...(search    ? { search }    : {}),
    },
  });

// Daily report recipient toggles
export const getReportRecipients = () =>
  api.get('/admin/query-history/report-recipients');

export const setAdminReportNotification = (email, enabled) =>
  api.patch(`/admin/query-history/report-recipients/admin/${encodeURIComponent(email)}`, { enabled });

export const setPmReportNotification = (email, enabled) =>
  api.patch(`/admin/query-history/report-recipients/pm/${encodeURIComponent(email)}`, { enabled });

export const sendReportNow = () =>
  api.post('/admin/query-history/send-report-now');

export const chatIntentMeet = (message) =>
  api.post('/admin/broadcasts/meet-intent', { message });

export const getBroadcasts = (status = '', limit = 50) =>
  api.get(`/admin/broadcasts?limit=${limit}${status ? `&status=${status}` : ''}`);

export const createBroadcast = (subject, bodyText, bodyHtml = '', scheduledAt = null, recipientEmails = null) =>
  api.post('/admin/broadcasts', { subject, body_text: bodyText, body_html: bodyHtml, scheduled_at: scheduledAt, recipient_emails: recipientEmails });

export const cancelBroadcast = (id) =>
  api.delete(`/admin/broadcasts/${id}`);

export const retryBroadcast = (id) =>
  api.post(`/admin/broadcasts/${id}/retry`);

export const sendBroadcastNow = (id) =>
  api.post(`/admin/broadcasts/${id}/send-now`);

export const getBroadcastRecipients = (includeExcluded = false) =>
  api.get(`/admin/broadcast-recipients?include_excluded=${includeExcluded}`);

export const addBroadcastRecipient = (email, name = '') =>
  api.post('/admin/broadcast-recipients', { email, name });

export const excludeBroadcastRecipient = (email) =>
  api.delete(`/admin/broadcast-recipients/${encodeURIComponent(email)}`);

export const restoreBroadcastRecipient = (email) =>
  api.post(`/admin/broadcast-recipients/${encodeURIComponent(email)}/restore`);

export const syncBroadcastRecipients = () =>
  api.post('/admin/broadcast-recipients/sync');

export const setBroadcastRecipientDepartment = (email, department) =>
  api.patch(`/admin/broadcast-recipients/${encodeURIComponent(email)}/department`, { department });
